import {
  faCheck,
  faKey,
  faTimes,
  faClock,
  faXmark,
  faPaperPlaneTop,
  faHandshake,
  faClipboardCheck,
  faEye,
  faCircleUser,
  faUserLock,
} from '@fortawesome/pro-regular-svg-icons'

import { StatusListingColors } from '@labrav/react-components'
import {
  AgreementDurationType,
  ResellerAgreementStatus,
} from '../../admin/modules/resaleAuthorizations/reducer'
import {
  MPPOResaleAuthStageToStatusRelationMapEnum,
  MPPOResaleAuthStages,
  ResaleAuthorizationType,
  ResaleAuthStages,
  ResaleAuthStageToStatusRelationMapEnum,
  MPPOResaleAuthAuditJson,
} from '../modules/receivedResellerAuthorization/reducer'
import { isEmpty } from 'lodash'
import { StatusItem } from '@labrav/react-components/lib/@types/v2/components/StatusListing'
import {
  AzureOfferPlan,
  AzurePricingType,
  RecurrentPriceModeType,
  ScheduleType,
} from '../modules/privateOffer/reducer'
import {
  getDiscountedPriceValue,
  isNonEmptyNumber,
} from '../../common/utils/getDiscountedPriceValue'
import { addMonths, addQuarters, addYears } from 'date-fns'
import { DateTime } from 'luxon'

export type StatusSItem = StatusItem & {
  sortOrder?: number
}

export const getResaleAuthStagesData = (
  auditJson: ResaleAuthStages,
  agreementStatus: ResellerAgreementStatus,
  resaleAuthDurationType: AgreementDurationType,
  agreementOrigin: ResaleAuthorizationType
) => {
  return Object.entries(auditJson).map(([status, completed]) => {
    let icon
    let color
    let background
    let borderColor
    let display = false
    let sortOrder = 0
    const isCreationFailed = agreementStatus === ResellerAgreementStatus.FAILED
    const tooltipText = '' // change to 'let' when needed
    // based on status name to resale auth status mapping
    if (
      agreementOrigin === ('CREATED' as ResaleAuthorizationType) &&
      status === ResaleAuthStageToStatusRelationMapEnum.CREATED &&
      !isCreationFailed
    ) {
      display = true
      icon = faCheck
      color = StatusListingColors.WHITE
      background = StatusListingColors.COMPLETED
      borderColor = StatusListingColors.COMPLETED
      sortOrder = 1
    }

    if (
      agreementOrigin === 'CREATED' &&
      status === ResaleAuthStageToStatusRelationMapEnum.FAILED &&
      isCreationFailed
    ) {
      display = true
      icon = faTimes
      color = StatusListingColors.WHITE
      background = StatusListingColors.REJECTED
      borderColor = StatusListingColors.REJECTED
      sortOrder = 1
    }

    if (status === ResaleAuthStageToStatusRelationMapEnum.ACTIVE) {
      display = isCreationFailed ? false : true
      icon = isCreationFailed ? faKey : faCheck
      background =
        agreementStatus === ResellerAgreementStatus.IN_PROGRESS
          ? StatusListingColors.WHITE
          : StatusListingColors.COMPLETED
      borderColor =
        agreementStatus === ResellerAgreementStatus.IN_PROGRESS
          ? StatusListingColors.GREY
          : StatusListingColors.COMPLETED
      color =
        agreementStatus === ResellerAgreementStatus.IN_PROGRESS
          ? StatusListingColors.GREY
          : StatusListingColors.WHITE
      sortOrder = 2
    }

    if (
      resaleAuthDurationType.toUpperCase() ===
        AgreementDurationType.SINGLE_USE &&
      status === ResaleAuthStageToStatusRelationMapEnum.COMPLETED
    ) {
      display =
        isCreationFailed ||
        agreementStatus === ResellerAgreementStatus.RESTRICTED
          ? false
          : true
      icon =
        agreementStatus === ResellerAgreementStatus.COMPLETED
          ? faCheck
          : faClock
      background =
        agreementStatus === ResellerAgreementStatus.COMPLETED
          ? StatusListingColors.COMPLETED
          : StatusListingColors.WHITE
      borderColor =
        agreementStatus === ResellerAgreementStatus.COMPLETED
          ? StatusListingColors.COMPLETED
          : StatusListingColors.GREY
      color =
        agreementStatus === ResellerAgreementStatus.COMPLETED
          ? StatusListingColors.WHITE
          : StatusListingColors.GREY
      sortOrder = 3
    }

    if (
      resaleAuthDurationType.toUpperCase() ===
        AgreementDurationType.SPECIFIC_TIME &&
      status === ResaleAuthStageToStatusRelationMapEnum.EXPIRED
    ) {
      display =
        isCreationFailed ||
        agreementStatus === ResellerAgreementStatus.RESTRICTED
          ? false
          : true
      icon =
        agreementStatus === ResellerAgreementStatus.EXPIRED ? faCheck : faClock
      background =
        agreementStatus === ResellerAgreementStatus.EXPIRED
          ? StatusListingColors.COMPLETED
          : StatusListingColors.WHITE
      borderColor =
        agreementStatus === ResellerAgreementStatus.EXPIRED
          ? StatusListingColors.COMPLETED
          : StatusListingColors.GREY
      color =
        agreementStatus === ResellerAgreementStatus.EXPIRED
          ? StatusListingColors.WHITE
          : StatusListingColors.GREY
      sortOrder = 3
    }

    if (status === ResaleAuthStageToStatusRelationMapEnum.DEACTIVATED) {
      icon = faTimes
      display =
        agreementStatus === ResellerAgreementStatus.RESTRICTED ? true : false
      // only displayed when status is deactivated
      background = StatusListingColors.REJECTED
      borderColor = StatusListingColors.REJECTED
      color = StatusListingColors.WHITE
      sortOrder = 3
    }

    return {
      status,
      completed,
      icon,
      color,
      background,
      display,
      sortOrder,
      tooltipText,
      borderColor,
    }
  })
}

export const getMPPOResaleAuthStagesData = (
  auditJson: MPPOResaleAuthStages,
  agreementStatus?: ResellerAgreementStatus,
  failedDate?: string | null
) => {
  const statges: StatusItem[] = Object.entries(auditJson).map(
    ([status, completed]) => {
      let icon
      let display = true
      let sortOrder = 0
      let completedDate = completed
      let tooltipText = ''

      let color = isEmpty(completed)
        ? StatusListingColors.GREY
        : StatusListingColors.WHITE
      let background = isEmpty(completed)
        ? StatusListingColors.WHITE
        : StatusListingColors.COMPLETED
      let borderColor = isEmpty(completed)
        ? StatusListingColors.BORDER
        : StatusListingColors.COMPLETED
      const isNextCompleted = getNextCompleted(status, auditJson, failedDate)

      switch (status) {
        case MPPOResaleAuthStageToStatusRelationMapEnum.offerCreationInProgress:
          icon = faCheck
          color = StatusListingColors.WHITE
          background = StatusListingColors.COMPLETED
          borderColor = StatusListingColors.COMPLETED
          sortOrder = 1
          break
        case MPPOResaleAuthStageToStatusRelationMapEnum.offerSentReseller:
          icon =
            isEmpty(completed) &&
            agreementStatus === ResellerAgreementStatus.OFFER_CREATION_FAILED
              ? faXmark
              : isEmpty(completed)
                ? faPaperPlaneTop
                : faCheck

          background =
            isEmpty(completed) &&
            agreementStatus === ResellerAgreementStatus.OFFER_CREATION_FAILED
              ? StatusListingColors.REJECTED
              : isEmpty(completed)
                ? StatusListingColors.WHITE
                : StatusListingColors.COMPLETED
          color =
            isEmpty(completed) &&
            agreementStatus === ResellerAgreementStatus.OFFER_CREATION_FAILED
              ? StatusListingColors.WHITE
              : isEmpty(completed)
                ? StatusListingColors.GREY
                : StatusListingColors.WHITE
          borderColor =
            isEmpty(completed) &&
            agreementStatus === ResellerAgreementStatus.OFFER_CREATION_FAILED
              ? StatusListingColors.REJECTED
              : isEmpty(completed)
                ? StatusListingColors.BORDER
                : StatusListingColors.COMPLETED
          completedDate =
            isEmpty(completed) &&
            agreementStatus === ResellerAgreementStatus.OFFER_CREATION_FAILED
              ? failedDate
              : completed
          sortOrder = 2
          break
        case MPPOResaleAuthStageToStatusRelationMapEnum.offerViewedReseller:
          icon = isEmpty(completed) ? faEye : faCheck
          sortOrder = 3
          break
        case MPPOResaleAuthStageToStatusRelationMapEnum.offerExtendedToBuyer:
          icon = isEmpty(completed) ? faCircleUser : faCheck
          sortOrder = 4
          break
        case MPPOResaleAuthStageToStatusRelationMapEnum.offerTermsAcceptedBuyer:
          icon = isEmpty(completed) ? faHandshake : faCheck
          sortOrder = 5
          tooltipText = 'Buyer is yet to create the Microsoft subscription.'
          break
        case MPPOResaleAuthStageToStatusRelationMapEnum.offerPurchaseComplete:
          icon = isEmpty(completed) ? faClipboardCheck : faCheck
          sortOrder = 6
          tooltipText = 'Buyer is yet to complete their account configuration.'
          break
        case MPPOResaleAuthStageToStatusRelationMapEnum.offerAccepted:
          icon = isEmpty(completed) ? faUserLock : faCheck
          sortOrder = 7
          tooltipText = 'Buyer has completed all steps to accept the offer.'
          break
        default:
          icon = faClipboardCheck
          display = false
      }

      return {
        status,
        completed: completedDate,
        icon,
        color,
        background,
        display,
        borderColor,
        sortOrder,
        isNextCompleted,
        tooltipText,
      }
    }
  )

  if (!auditJson.Deactivated) {
    return statges
  }

  const competedStages = statges
    ?.filter(obj => obj.display === true && obj.completed)
    ?.sort((a, b) => (a?.sortOrder || 0) - (b?.sortOrder || 0))

  competedStages[competedStages?.length - 1] = {
    ...competedStages[competedStages?.length - 1],
    verticalLineColor: StatusListingColors.REJECTED,
    isNextCompleted: true,
  }
  return [...competedStages].concat([
    {
      status: MPPOResaleAuthStageToStatusRelationMapEnum.offerRestricted,
      completed: auditJson.Deactivated,
      icon: faXmark,
      color: StatusListingColors.WHITE,
      background: StatusListingColors.REJECTED,
      display: true,
      borderColor: StatusListingColors.REJECTED,
      sortOrder: competedStages?.length + 1,
      isNextCompleted: true,
    },
  ])
}

const getNextCompleted = (
  keyName: string | null,
  auditJson: MPPOResaleAuthStages,
  failedDate?: string | null
) => {
  switch (keyName) {
    case MPPOResaleAuthStageToStatusRelationMapEnum.offerCreationInProgress:
      return !!auditJson?.['Sent to reseller'] || !!failedDate
    case MPPOResaleAuthStageToStatusRelationMapEnum.offerSentReseller:
      return !!auditJson?.['Viewed by reseller']
    case MPPOResaleAuthStageToStatusRelationMapEnum.offerViewedReseller:
      return !!auditJson?.['Extended to buyer']
    case MPPOResaleAuthStageToStatusRelationMapEnum.offerExtendedToBuyer:
      return !!auditJson?.['Pending purchase']
    case MPPOResaleAuthStageToStatusRelationMapEnum.offerTermsAcceptedBuyer:
      return !!auditJson?.['Pending account configuration']
    case MPPOResaleAuthStageToStatusRelationMapEnum.offerPurchaseComplete:
      return !!auditJson?.['Accepted']
    default:
      return false
  }
}

export const getPriceTitleAndValue = ({
  azurePricingType,
  recurrentPriceMode,
  plan,
}: {
  azurePricingType?: AzurePricingType
  recurrentPriceMode?: RecurrentPriceModeType
  plan?: AzureOfferPlan
}) => {
  const isFlexiblePayment =
    plan?.pricing?.recurrentPrice?.prices[0]?.installments?.schedule?.length
  const priceValue = isFlexiblePayment
    ? plan?.pricing?.recurrentPrice?.prices[0]?.installments?.tcv
    : plan?.pricing?.recurrentPrice?.prices[0]?.pricePerPaymentInUsd

  if (azurePricingType === AzurePricingType.LABRA_PERCENTAGE) {
    if (recurrentPriceMode === RecurrentPriceModeType.PERUSER) {
      return {
        title: 'Discount price per user',
        value: (isFlexiblePayment ? priceValue : priceValue) || undefined,
      }
    }
    return {
      title: 'Discount price',
      value: (isFlexiblePayment ? priceValue : priceValue) || undefined,
    }
  }

  if (recurrentPriceMode === RecurrentPriceModeType.PERUSER) {
    return { title: 'Price per user', value: priceValue || undefined }
  }
  return { title: 'Total contract value', value: priceValue || undefined }
}

const calculateInstallmentDate = (
  startDate: string,
  flexiblePaymentType: ScheduleType,
  installmentNumber: number
): string => {
  let calculatedDate: Date
  switch (flexiblePaymentType) {
    case ScheduleType.MONTHLY:
      calculatedDate = addMonths(new Date(startDate), installmentNumber)
      break
    case ScheduleType.QUARTERLY:
      calculatedDate = addQuarters(new Date(startDate), installmentNumber)
      break
    case ScheduleType.YEARLY:
      calculatedDate = addYears(new Date(startDate), installmentNumber)
      break
    default:
      calculatedDate = new Date(startDate)
  }
  const formattedDate = DateTime.fromJSDate(calculatedDate).toISODate()
  return formattedDate || calculatedDate.toISOString().split('T')[0] // fallback
}

const calculateNumberOfInstallments = (
  flexiblePaymentType: ScheduleType,
  serviceLength?: number
): number => {
  if (serviceLength) {
    switch (flexiblePaymentType) {
      case ScheduleType.MONTHLY:
        return Math.ceil(serviceLength / 1)
      case ScheduleType.QUARTERLY:
        return Math.ceil(serviceLength / 3)
      case ScheduleType.YEARLY:
        return Math.ceil(serviceLength / 12) > 1
          ? Math.ceil(serviceLength / 12)
          : 2
      default:
        return 2
    }
  }
  return 2
}

export const getInstallmentsBasedOnSchedule = ({
  durationInMonths,
  now,
  paymentSchedule,
  tcv,
}: {
  tcv: number
  durationInMonths: number
  paymentSchedule: ScheduleType
  now: string
  variableStartDate?: boolean
}) => {
  const quantityOfInstallments = calculateNumberOfInstallments(
    paymentSchedule || ScheduleType.CUSTOM,
    durationInMonths
  )

  const base = Math.floor(tcv / quantityOfInstallments)
  const remainder = tcv - base * quantityOfInstallments

  return Array.from({ length: quantityOfInstallments }).map((_, index) => {
    return {
      pricePerPaymentInUsd: base + (index === 0 ? remainder : 0),
      chargeDate:
        index === 0
          ? null
          : calculateInstallmentDate(
              now || new Date().toISOString(),
              paymentSchedule || ScheduleType.CUSTOM,
              index
            ),
      isImmediateInstallment: index === 0,
    }
  })
}
